import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

const RecentActivityFeed = ({ activities }) => {
  const { t } = useTranslation();
  const navigate = useNavigate(); // Initialize useNavigate
  const getActivityIcon = (type) => {
    switch (type) {
      case 'asset_added':
        return 'Plus';
      case 'asset_updated':
        return 'Edit';
      case 'asset_checked_out':
        return 'ArrowRight';
      case 'asset_checked_in':
        return 'ArrowLeft';
      case 'maintenance_logged':
        return 'Wrench';
      case 'asset_retired':
        return 'Archive';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'asset_added':
        return 'text-emerald-500 dark:text-emerald-300';
      case 'asset_updated':
        return 'text-blue-500 dark:text-blue-300';
      case 'asset_checked_out':
        return 'text-amber-500 dark:text-amber-300';
      case 'asset_checked_in':
        return 'text-emerald-500 dark:text-emerald-300';
      case 'maintenance_logged':
        return 'text-blue-500 dark:text-blue-300';
      case 'asset_retired':
        return 'text-slate-400 dark:text-slate-300';
      default:
        return 'text-foreground';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return t('time.just_now');
    if (diffInMinutes < 60) return `${diffInMinutes}m ${t('time.ago')}`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ${t('time.ago')}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ${t('time.ago')}`;
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t('dashboard.recent_activity_title')}</h3>
        <p className="text-sm text-foreground opacity-70">{t('dashboard.recent_activity_subtitle')}</p>
      </div>
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities?.map((activity) => (
          <div key={activity?.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
            <div className={`p-2 rounded-full bg-muted dark:bg-slate-600 ${getActivityColor(activity?.type)}`}>
              <Icon name={getActivityIcon(activity?.type)} size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{activity?.description}</p>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-xs text-foreground opacity-70">{t('dashboard.by_user', { user: activity?.user })}</span>
                <span className="text-xs text-foreground opacity-70">•</span>
                <span className="text-xs text-foreground opacity-70">{formatTimeAgo(activity?.timestamp)}</span>
              </div>
              {activity?.assetId && (
                <p className="text-xs text-foreground opacity-70 mt-1">{t('dashboard.asset_id_label', { id: activity?.assetId })}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <button 
          onClick={() => navigate('/all-activities')} // Add onClick handler
          className="text-sm text-blue-500 dark:text-blue-300 hover:text-blue-600 dark:hover:text-blue-200 font-medium transition-colors"
        >
          {t('dashboard.view_all_activity')}
        </button>
      </div>
    </div>
  );
};

export default RecentActivityFeed;